import React from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  BadgeEuro,
  CalendarDays,
  Check,
  ChevronDown,
  CircleHelp,
  Euro,
  HandCoins,
  Info,
  Landmark,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
  LoaderCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../../../components/feedback/ToastProvider";
import "./RenovationProjectSubsidiesGrantsStep.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   STATUS
   ========================================================= */

const STATUS_OPTIONS = [
  {
    value: "NOT_APPLIED",
    key: "statuses.NOT_APPLIED",
    fallback: "Not applied",
  },
  {
    value: "PENDING",
    key: "statuses.PENDING",
    fallback: "Pending",
  },
  {
    value: "GRANTED",
    key: "statuses.GRANTED",
    fallback: "Granted",
  },
  {
    value: "REJECTED",
    key: "statuses.REJECTED",
    fallback: "Rejected",
  },
];

const EMPTY_FORM = {
  funding_type_id: "",
  status: "NOT_APPLIED",
  application_date: "",
  awarded_date: "",
  denial_date: "",
  granted_amount: "",
  notes: "",
};

/* =========================================================
   HELPERS
   ========================================================= */

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "SUBSIDIES_GRANTS") || {
      step_code: "SUBSIDIES_GRANTS",
      step_order: 6,
      status: "NOT_STARTED",
      is_optional: true,
      visible_to: "HB_OSS",
      editable_by: "HB_OSS",
    }
  );
}

function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function normalizeSupportItem(item, index) {
  const fundingType =
    item?.funding_type || item?.source_type || item?.type || null;

  const origin = String(
    item?.origin ||
      item?.created_from ||
      (item?.provided_by_lender ? "LENDER" : "PROJECT"),
  ).toUpperCase();

  const isLenderProvided = Boolean(
    item?.provided_by_lender || origin === "LENDER",
  );

  return {
    id: item?.id ?? `existing-${index}`,
    subsidy_grant_id: item?.subsidy_grant_id ?? item?.id ?? null,
    funding_source_id: item?.funding_source_id ?? null,
    funding_type_id: item?.funding_type_id ?? fundingType?.id ?? "",
    funding_type_name:
      item?.funding_type_name ||
      fundingType?.name ||
      item?.name ||
      item?.title ||
      "—",
    category: normalizeCategory(
      item?.category ||
        fundingType?.category ||
        fundingType?.source_type ||
        item?.source_type_code,
    ),
    provider_name:
      item?.provider_name || item?.provider || fundingType?.provider_name || "",
    status: String(
      item?.status ||
        (item?.granted
          ? "GRANTED"
          : item?.rejected
            ? "REJECTED"
            : item?.applied
              ? "PENDING"
              : "NOT_APPLIED"),
    ).toUpperCase(),
    application_date: item?.application_date || item?.date_applied || "",
    awarded_date: item?.awarded_date || item?.date_awarded || "",
    denial_date: item?.denial_date || item?.date_denial || "",
    granted_amount: item?.granted_amount ?? item?.amount_granted ?? "",
    notes: item?.notes || "",
    origin,
    added_by_role: String(
      item?.added_by_role || item?.created_by_role || "",
    ).toUpperCase(),
    is_lender_provided: isLenderProvided,
    can_edit:
      item?.can_edit !== undefined ? Boolean(item.can_edit) : !isLenderProvided,
    can_delete:
      item?.can_delete !== undefined
        ? Boolean(item.can_delete)
        : !isLenderProvided,
  };
}

function normalizeFundingType(item, index) {
  return {
    id: item?.id ?? item?.value ?? `type-${index}`,

    name: item?.name || item?.label || item?.title || "Funding type",

    category: normalizeCategory(
      item?.category || item?.source_type || item?.type,
    ),

    provider_name: item?.provider_name || item?.provider || "",

    description: item?.description || "",

    active: item?.active ?? item?.is_active ?? true,
  };
}

function formatCurrency(value) {
  if (value === "" || value === null || value === undefined) {
    return "—";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return new Intl.NumberFormat("en-EU", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function statusClass(status) {
  return String(status || "NOT_APPLIED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function stepStatusClass(status) {
  return String(status || "NOT_STARTED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function supportCategoryClass(category) {
  return String(category || "OTHER")
    .toLowerCase()
    .replaceAll("_", "-");
}

/* =========================================================
   TOOLTIP
   ========================================================= */

function FieldTooltip({ text, label }) {
  return (
    <span className="subsidies-tooltip-wrap">
      <button
        type="button"
        className="subsidies-tooltip-trigger"
        aria-label={label}
      >
        <CircleHelp size={16} />
      </button>

      <span className="subsidies-tooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

/* =========================================================
   FIELD LABEL
   ========================================================= */

function FieldLabel({ children, tooltip, t, required = false }) {
  return (
    <span className="subsidies-field-label">
      <span className="subsidies-field-label__text">
        {children}

        {tooltip ? (
          <FieldTooltip
            text={tooltip}
            label={t("common.moreInformation", "More information")}
          />
        ) : null}
      </span>

      {required ? (
        <span className="subsidies-required-pill">
          {t("common.required", "Required")}
        </span>
      ) : null}
    </span>
  );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function RenovationProjectSubsidiesGrantsStep() {
  const { t } = useTranslation("renovation-project-subsidies-grants");

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const navigate = useNavigate();

  const { project, steps, basePath, viewerRole, refreshProject } =
    useOutletContext();

  const step = findStep(steps);

  /* =========================================================
   STEP 6 SERVER STATE
   ========================================================= */

  const [fundingTypes, setFundingTypes] = React.useState([]);

  const [items, setItems] = React.useState([]);

  const [stepStatus, setStepStatus] = React.useState(
    step.status || "NOT_STARTED",
  );

  const [loading, setLoading] = React.useState(true);

  const [loadError, setLoadError] = React.useState("");

  const [savingItem, setSavingItem] = React.useState(false);
  const [deletingItem, setDeletingItem] = React.useState(false);
  const [completingStep, setCompletingStep] = React.useState(false);

  /* =========================================================
   ADDABLE FUNDING TYPES
   ========================================================= */

  const addableFundingTypes = React.useMemo(
    () =>
      fundingTypes.filter((item) =>
        ["SUBSIDY", "GRANT", "PUBLIC_GRANT", "PUBLIC_SUBSIDY"].includes(
          normalizeCategory(item.category),
        ),
      ),
    [fundingTypes],
  );

  /* =========================================================
   API HELPERS
   ========================================================= */

  const normalizeApiErrors = React.useCallback((errors) => {
    if (!errors || typeof errors !== "object") {
      return {};
    }

    return Object.entries(errors).reduce((result, [key, value]) => {
      if (Array.isArray(value)) {
        result[key] = value.join(" ");
      } else if (value !== null && value !== undefined) {
        result[key] = String(value);
      }

      return result;
    }, {});
  }, []);

  const requestJson = React.useCallback(
    async (url, options = {}) => {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}${url}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          ...(options.body ? { "Content-Type": "application/json" } : {}),
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(
          data?.detail ||
            t("errors.requestFailed", "The request could not be completed."),
        );

        error.status = response.status;
        error.payload = data;
        throw error;
      }

      return data;
    },
    [getAccessTokenSilently, t],
  );

  const applyStepResponse = React.useCallback(
    (data) => {
      const catalogue = Array.isArray(data?.funding_type_catalog)
        ? data.funding_type_catalog
        : [];

      const loadedItems = Array.isArray(data?.items) ? data.items : [];

      setFundingTypes(
        catalogue.map(normalizeFundingType).filter((item) => item.active),
      );

      setItems(loadedItems.map(normalizeSupportItem));
      setStepStatus(data?.step_status || step.status || "NOT_STARTED");
    },
    [step.status],
  );

  /* =========================================================
   LOAD STEP 6
   ========================================================= */

  const loadSubsidiesGrants = React.useCallback(async () => {
    if (!project?.id) {
      return;
    }

    setLoading(true);
    setLoadError("");

    try {
      const data = await requestJson(
        `/api/projects/${project.id}/subsidies-grants/`,
      );

      applyStepResponse(data);
    } catch (error) {
      console.error("Failed to load Step 6:", error);
      setLoadError(
        error?.message ||
          t("errors.loadFailed", "Failed to load subsidies and grants."),
      );
    } finally {
      setLoading(false);
    }
  }, [applyStepResponse, project?.id, requestJson, t]);

  React.useEffect(() => {
    loadSubsidiesGrants();
  }, [loadSubsidiesGrants]);

  /* =======================================================
     MODAL
     ======================================================= */

  const [modalOpen, setModalOpen] = React.useState(false);

  const [modalClosing, setModalClosing] = React.useState(false);

  const [editingId, setEditingId] = React.useState(null);

  const [form, setForm] = React.useState(EMPTY_FORM);

  const [formErrors, setFormErrors] = React.useState({});

  const closeTimerRef = React.useRef(null);

  /* =======================================================
     DELETE MODAL
     ======================================================= */

  const [deleteTarget, setDeleteTarget] = React.useState(null);

  const [deleteClosing, setDeleteClosing] = React.useState(false);

  const deleteTimerRef = React.useRef(null);

  /* =======================================================
     PERMISSIONS
     ======================================================= */

  const canEdit = ["HB", "OSS"].includes(viewerRole);

  /* =======================================================
     MODAL OPEN
     ======================================================= */

  const openCreateModal = () => {
    setEditingId(null);

    setForm({
      ...EMPTY_FORM,
    });

    setFormErrors({});

    setModalClosing(false);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.id);

    setForm({
      funding_type_id: String(item.funding_type_id || ""),

      status: item.status || "NOT_APPLIED",

      application_date: item.application_date || "",

      awarded_date: item.awarded_date || "",

      denial_date: item.denial_date || "",

      granted_amount: item.granted_amount ?? "",

      notes: item.notes || "",
    });

    setFormErrors({});

    setModalClosing(false);
    setModalOpen(true);
  };

  /* =======================================================
     CLOSE WITH EXIT ANIMATION
     ======================================================= */

  const closeModal = () => {
    if (!modalOpen) {
      return;
    }

    setModalClosing(true);

    window.clearTimeout(closeTimerRef.current);

    closeTimerRef.current = window.setTimeout(() => {
      setModalOpen(false);
      setModalClosing(false);
      setEditingId(null);
      setFormErrors({});
    }, 190);
  };

  React.useEffect(
    () => () => {
      window.clearTimeout(closeTimerRef.current);

      window.clearTimeout(deleteTimerRef.current);
    },
    [],
  );

  /* =======================================================
     BODY SCROLL LOCK
     ======================================================= */

  React.useEffect(() => {
    if (!modalOpen && !deleteTarget) {
      return undefined;
    }

    const previous = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, [deleteTarget, modalOpen]);

  /* =======================================================
     FORM UPDATE
     ======================================================= */

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => ({
      ...current,
      [field]: undefined,
    }));
  };

  /* =======================================================
     CONDITIONAL STATUS FLAGS
     ======================================================= */

  const isGranted = form.status === "GRANTED";

  const isRejected = form.status === "REJECTED";

  const hasApplied = ["PENDING", "GRANTED", "REJECTED"].includes(form.status);

  /* =======================================================
     SAVE MODAL ITEM - POST / PATCH
     ======================================================= */

  const saveItem = async () => {
    if (savingItem) {
      return;
    }

    const errors = {};

    if (!form.funding_type_id) {
      errors.funding_type_id = t(
        "validation.fundingTypeRequired",
        "Select a funding type.",
      );
    }

    if (hasApplied && !form.application_date) {
      errors.application_date = t(
        "validation.applicationDateRequired",
        "Enter the application date.",
      );
    }

    if (isGranted && !form.awarded_date) {
      errors.awarded_date = t(
        "validation.awardedDateRequired",
        "Enter the award date.",
      );
    }

    if (isRejected && !form.denial_date) {
      errors.denial_date = t(
        "validation.denialDateRequired",
        "Enter the denial date.",
      );
    }

    if (isGranted && form.granted_amount === "") {
      errors.granted_amount = t(
        "validation.grantedAmountRequired",
        "Enter the granted amount.",
      );
    }

    if (form.granted_amount !== "" && Number(form.granted_amount) < 0) {
      errors.granted_amount = t(
        "validation.amountNonNegative",
        "Amount cannot be negative.",
      );
    }

    if (
      form.granted_amount !== "" &&
      !Number.isFinite(Number(form.granted_amount))
    ) {
      errors.granted_amount = t(
        "validation.invalidAmount",
        "Enter a valid amount.",
      );
    }

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      funding_type_id: form.funding_type_id,
      status: form.status,
      application_date: hasApplied ? form.application_date : "",
      awarded_date: isGranted ? form.awarded_date : "",
      denial_date: isRejected ? form.denial_date : "",
      granted_amount: isGranted ? form.granted_amount : "",
      notes: form.notes,
    };

    setSavingItem(true);
    setFormErrors({});

    try {
      const isEditing = Boolean(editingId);

      const url = isEditing
        ? `/api/projects/${project.id}/subsidies-grants/${editingId}/`
        : `/api/projects/${project.id}/subsidies-grants/`;

      const data = await requestJson(url, {
        method: isEditing ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });

      applyStepResponse(data);
      await refreshProject?.();

      showToast({
        type: "success",
        title: isEditing
          ? t("toasts.updatedTitle", "Subsidy updated")
          : t("toasts.addedTitle", "Subsidy added"),
        message: isEditing
          ? t(
              "toasts.updatedMessage",
              "The subsidy information was updated successfully.",
            )
          : t(
              "toasts.addedMessage",
              "The subsidy was added to the renovation project.",
            ),
      });

      closeModal();
    } catch (error) {
      console.error("Failed to save subsidy:", error);

      const backendErrors = normalizeApiErrors(error?.payload?.errors);

      if (Object.keys(backendErrors).length) {
        setFormErrors(backendErrors);
      }

      showToast({
        type: "error",
        title: t("toasts.saveFailedTitle", "Could not save subsidy"),
        message:
          error?.message ||
          t(
            "toasts.saveFailedMessage",
            "Please check the information and try again.",
          ),
      });
    } finally {
      setSavingItem(false);
    }
  };

  /* =======================================================
     DELETE
     ======================================================= */

  const requestDelete = (item) => {
    setDeleteClosing(false);

    setDeleteTarget(item);
  };

  const closeDeleteModal = () => {
    if (!deleteTarget) {
      return;
    }

    setDeleteClosing(true);

    window.clearTimeout(deleteTimerRef.current);

    deleteTimerRef.current = window.setTimeout(() => {
      setDeleteTarget(null);

      setDeleteClosing(false);
    }, 180);
  };

  const confirmDelete = async () => {
    if (
      deletingItem ||
      !deleteTarget?.id ||
      deleteTarget?.is_lender_provided ||
      deleteTarget?.can_delete === false
    ) {
      return;
    }

    setDeletingItem(true);

    try {
      const data = await requestJson(
        `/api/projects/${project.id}/subsidies-grants/${deleteTarget.id}/`,
        {
          method: "DELETE",
        },
      );

      applyStepResponse(data);
      await refreshProject?.();

      showToast({
        type: "success",
        title: t("toasts.deletedTitle", "Subsidy removed"),
        message: t(
          "toasts.deletedMessage",
          "The subsidy was removed from the renovation project.",
        ),
      });

      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete subsidy:", error);

      showToast({
        type: "error",
        title: t("toasts.deleteFailedTitle", "Could not remove subsidy"),
        message:
          error?.message ||
          t("toasts.deleteFailedMessage", "The subsidy could not be removed."),
      });
    } finally {
      setDeletingItem(false);
    }
  };

  /* =======================================================
     FUNDING TYPE DISPLAY
     ======================================================= */

  const categoryLabel = (category) => {
    const normalized = normalizeCategory(category);

    return t(
      `categories.${normalized}`,
      normalized.replaceAll("_", " ").toLowerCase(),
    );
  };

  const originLabel = (item) => {
    if (item.is_lender_provided || item.origin === "LENDER") {
      return t("origins.LENDER", "Provided by lender");
    }

    if (item.added_by_role === "OSS") {
      return t("origins.OSS", "Added by OSS");
    }

    if (item.added_by_role === "HB") {
      return t("origins.HB", "Added by Homebuyer");
    }

    return t("origins.PROJECT", "Project funding");
  };

  const displayStatus = stepStatus || step.status || "NOT_STARTED";

  /* =======================================================
     COMPLETE OPTIONAL STEP AND CONTINUE
     ======================================================= */

  const goNext = async () => {
    if (completingStep || !project?.id) {
      return;
    }

    setCompletingStep(true);

    try {
      const data = await requestJson(
        `/api/projects/${project.id}/subsidies-grants/complete/`,
        {
          method: "POST",
        },
      );

      applyStepResponse(data);
      await refreshProject?.();

      navigate(`${basePath}/${project.id}/financing`);
    } catch (error) {
      console.error("Failed to complete Step 6:", error);

      showToast({
        type: "error",
        title: t(
          "toasts.completeFailedTitle",
          "Could not continue to Financing",
        ),
        message:
          error?.message ||
          t(
            "toasts.completeFailedMessage",
            "The Subsidies & grants stage could not be completed.",
          ),
      });
    } finally {
      setCompletingStep(false);
    }
  };

  return (
    <>
      <article className="subsidies-step">
        {/* =================================================
            HEADER
            ================================================= */}

        <header className="subsidies-step__header">
          <div className="subsidies-step__heading">
            <span className="subsidies-step__eyebrow">
              <HandCoins size={15} />

              {t("header.step", "Step 6")}

              <span className="subsidies-step__optional">
                {t("header.optional", "Optional")}
              </span>
            </span>

            <h1>{t("header.title", "Subsidies & grants")}</h1>

            <p>
              {t(
                "header.subtitle",
                "Track public subsidies, grants and lender-provided support linked to this renovation.",
              )}
            </p>
          </div>

          <div className="subsidies-step__status">
            <span className="subsidies-step__stage-count">
              {t("header.stage", "Stage {{current}} of {{total}}", {
                current: step.step_order || 6,
                total: 8,
              })}
            </span>

            <span
              className={
                `subsidies-step__status-pill ` +
                `subsidies-step__status-pill--${stepStatusClass(displayStatus)}`
              }
            >
              {t(
                `stepStatuses.${displayStatus}`,
                String(displayStatus).replaceAll("_", " ").toLowerCase(),
              )}
            </span>
          </div>
        </header>

        {/* =================================================
            OPTIONAL NOTE
            ================================================= */}

        <div className="subsidies-optional-note">
          <Info size={18} />

          <div>
            <strong>
              {t("optionalNote.title", "This stage is optional.")}
            </strong>

            <span>
              {t(
                "optionalNote.description",
                "You can continue to Financing even when no subsidy or grant has been identified. Funding support can be added or updated later.",
              )}
            </span>
          </div>
        </div>

        {/* =================================================
            SUPPORT SECTION
            ================================================= */}

        <section className="subsidies-section">
          <div className="subsidies-section__header">
            <div className="subsidies-section__heading">
              <span className="subsidies-section__icon">
                <BadgeEuro size={21} />
              </span>

              <div>
                <div className="subsidies-section__title-row">
                  <h2>{t("support.title", "Funding support")}</h2>

                  <FieldTooltip
                    text={t(
                      "support.tooltip",
                      "This list may include subsidies and soft loans already identified by the lender, together with subsidies or grants added by the Homebuyer or OSS.",
                    )}
                    label={t("common.moreInformation", "More information")}
                  />
                </div>

                <p>
                  {t(
                    "support.subtitle",
                    "Overview of public support and preferential funding linked to this renovation.",
                  )}
                </p>
              </div>
            </div>

            {canEdit ? (
              <button
                type="button"
                className="subsidies-add-button"
                onClick={openCreateModal}
              >
                <Plus size={17} />

                {t("buttons.addSubsidy", "Add subsidy")}
              </button>
            ) : null}
          </div>

          {loading ? (
            <div className="subsidies-empty">
              <span className="subsidies-empty__icon">
                <LoaderCircle size={26} />
              </span>

              <div>
                <strong>{t("loading.title", "Loading funding support")}</strong>
                <p>
                  {t(
                    "loading.description",
                    "Loading subsidies, grants and lender-provided funding sources...",
                  )}
                </p>
              </div>
            </div>
          ) : loadError ? (
            <div className="subsidies-empty">
              <span className="subsidies-empty__icon">
                <Info size={26} />
              </span>

              <div>
                <strong>
                  {t("errors.loadTitle", "Funding support could not be loaded")}
                </strong>
                <p>{loadError}</p>
              </div>

              <button type="button" onClick={loadSubsidiesGrants}>
                {t("buttons.retry", "Try again")}
              </button>
            </div>
          ) : items.length ? (
            <>
              {/* =============================================
                  DESKTOP TABLE
                  ============================================= */}

              <div className="subsidies-table-wrap">
                <table className="subsidies-table">
                  <thead>
                    <tr>
                      <th>{t("table.support", "Funding support")}</th>

                      <th>{t("table.type", "Type")}</th>

                      <th>{t("table.origin", "Origin")}</th>

                      <th>{t("table.status", "Status")}</th>

                      <th>{t("table.application", "Application")}</th>

                      <th>{t("table.amount", "Granted amount")}</th>

                      <th aria-label={t("table.actions", "Actions")} />
                    </tr>
                  </thead>

                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="subsidies-support-name">
                            <span className="subsidies-support-name__icon">
                              {item.category === "SOFT_LOAN" ? (
                                <Landmark size={16} />
                              ) : (
                                <HandCoins size={16} />
                              )}
                            </span>

                            <div>
                              <strong>{item.funding_type_name}</strong>

                              {item.provider_name ? (
                                <small>{item.provider_name}</small>
                              ) : null}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={
                              `subsidies-type-pill ` +
                              `subsidies-type-pill--${supportCategoryClass(
                                item.category,
                              )}`
                            }
                          >
                            {categoryLabel(item.category)}
                          </span>
                        </td>

                        <td>
                          <span className="subsidies-origin">
                            {originLabel(item)}
                          </span>
                        </td>

                        <td>
                          <span
                            className={
                              `subsidies-status-pill ` +
                              `subsidies-status-pill--${statusClass(
                                item.status,
                              )}`
                            }
                          >
                            {t(`statuses.${item.status}`, item.status)}
                          </span>
                        </td>

                        <td>{formatDate(item.application_date)}</td>

                        <td>
                          <strong className="subsidies-amount">
                            {item.status === "GRANTED"
                              ? formatCurrency(item.granted_amount)
                              : "—"}
                          </strong>
                        </td>

                        <td>
                          {canEdit && (item.can_edit || item.can_delete) ? (
                            <div className="subsidies-row-actions">
                              {item.can_edit ? (
                                <button
                                  type="button"
                                  onClick={() => openEditModal(item)}
                                  title={t("buttons.edit", "Edit")}
                                >
                                  <Pencil size={15} />
                                </button>
                              ) : null}

                              {item.can_delete ? (
                                <button
                                  type="button"
                                  className="is-danger"
                                  onClick={() => requestDelete(item)}
                                  title={t("buttons.delete", "Delete")}
                                >
                                  <Trash2 size={15} />
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* =============================================
                  MOBILE CARDS
                  ============================================= */}

              <div className="subsidies-mobile-list">
                {items.map((item) => (
                  <article key={item.id} className="subsidies-mobile-card">
                    <div className="subsidies-mobile-card__header">
                      <div>
                        <strong>{item.funding_type_name}</strong>

                        {item.provider_name ? (
                          <span>{item.provider_name}</span>
                        ) : null}
                      </div>

                      <span
                        className={
                          `subsidies-status-pill ` +
                          `subsidies-status-pill--${statusClass(item.status)}`
                        }
                      >
                        {t(`statuses.${item.status}`, item.status)}
                      </span>
                    </div>

                    <div className="subsidies-mobile-card__meta">
                      <div>
                        <span>{t("table.type", "Type")}</span>

                        <strong>{categoryLabel(item.category)}</strong>
                      </div>

                      <div>
                        <span>{t("table.origin", "Origin")}</span>

                        <strong>{originLabel(item)}</strong>
                      </div>

                      <div>
                        <span>{t("table.application", "Application")}</span>

                        <strong>{formatDate(item.application_date)}</strong>
                      </div>

                      <div>
                        <span>{t("table.amount", "Granted amount")}</span>

                        <strong>
                          {item.status === "GRANTED"
                            ? formatCurrency(item.granted_amount)
                            : "—"}
                        </strong>
                      </div>
                    </div>
                    {canEdit && (item.can_edit || item.can_delete) ? (
                      <div className="subsidies-mobile-card__actions">
                        {item.can_edit ? (
                          <button
                            type="button"
                            onClick={() => openEditModal(item)}
                            title={t("buttons.edit", "Edit")}
                          >
                            <Pencil size={15} />
                          </button>
                        ) : null}

                        {item.can_delete ? (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => requestDelete(item)}
                            title={t("buttons.delete", "Delete")}
                          >
                            <Trash2 size={15} />
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="subsidies-empty">
              <span className="subsidies-empty__icon">
                <HandCoins size={26} />
              </span>

              <div>
                <strong>
                  {t("empty.title", "No subsidies or grants added yet")}
                </strong>

                <p>
                  {t(
                    "empty.description",
                    "If public funding is identified later, the Homebuyer or OSS can return to this stage and add it.",
                  )}
                </p>
              </div>

              {canEdit ? (
                <button type="button" onClick={openCreateModal}>
                  <Plus size={16} />

                  {t("buttons.addSubsidy", "Add subsidy")}
                </button>
              ) : null}
            </div>
          )}
        </section>

        {/* =================================================
            CONTINUE
            ================================================= */}

        <button
          type="button"
          className="subsidies-next"
          onClick={goNext}
          disabled={completingStep}
        >
          {completingStep ? <LoaderCircle size={17} /> : <Save size={17} />}

          {completingStep
            ? t("buttons.completing", "Saving...")
            : t("buttons.continue", "Save and continue to Financing")}
        </button>
      </article>

      {/* ===================================================
          ADD / EDIT MODAL
          =================================================== */}

      {modalOpen
        ? createPortal(
            <div
              className={`subsidies-modal-backdrop ${
                modalClosing ? "is-closing" : ""
              }`}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeModal();
                }
              }}
            >
              <div
                className={`subsidies-modal ${
                  modalClosing ? "is-closing" : ""
                }`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="subsidies-modal-title"
              >
                <div className="subsidies-modal__header">
                  <div>
                    <span className="subsidies-modal__icon">
                      <HandCoins size={20} />
                    </span>

                    <div>
                      <h2 id="subsidies-modal-title">
                        {editingId
                          ? t("modal.editTitle", "Update subsidy")
                          : t("modal.addTitle", "Add subsidy")}
                      </h2>

                      <p>
                        {t(
                          "modal.subtitle",
                          "Select an admin-defined funding type and record its current application status.",
                        )}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="subsidies-modal__close"
                    onClick={closeModal}
                    aria-label={t("buttons.close", "Close")}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="subsidies-modal__body">
                  {/* FUNDING TYPE */}

                  <label className="subsidies-field">
                    <FieldLabel
                      t={t}
                      required
                      tooltip={t(
                        "fields.fundingType.tooltip",
                        "Funding types are created centrally by the Platform Administrator. Only active subsidy or grant types can be selected here.",
                      )}
                    >
                      {t("fields.fundingType.label", "Funding type")}
                    </FieldLabel>

                    <div className="subsidies-select">
                      <select
                        value={form.funding_type_id}
                        onChange={(event) =>
                          updateForm("funding_type_id", event.target.value)
                        }
                      >
                        <option value="">
                          {t(
                            "fields.fundingType.placeholder",
                            "Select funding type",
                          )}
                        </option>

                        {(editingId ? fundingTypes : addableFundingTypes).map(
                          (type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                              {type.provider_name
                                ? ` — ${type.provider_name}`
                                : ""}
                            </option>
                          ),
                        )}
                      </select>

                      <ChevronDown size={17} />
                    </div>

                    {formErrors.funding_type_id ? (
                      <span className="subsidies-field-error">
                        {formErrors.funding_type_id}
                      </span>
                    ) : null}

                    {!addableFundingTypes.length && !editingId ? (
                      <span className="subsidies-field-help">
                        {t(
                          "fields.fundingType.emptyCatalogue",
                          "No active subsidy or grant funding types are currently available. They must first be created by the Platform Administrator.",
                        )}
                      </span>
                    ) : null}
                  </label>

                  {/* STATUS */}

                  <label className="subsidies-field">
                    <FieldLabel
                      t={t}
                      tooltip={t(
                        "fields.status.tooltip",
                        "Select the current stage of the subsidy application. Additional fields are shown depending on the selected status.",
                      )}
                    >
                      {t("fields.status.label", "Application status")}
                    </FieldLabel>

                    <div className="subsidies-select">
                      <select
                        value={form.status}
                        onChange={(event) =>
                          updateForm("status", event.target.value)
                        }
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.key, option.fallback)}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={17} />
                    </div>
                  </label>

                  {/* APPLICATION DATE */}

                  {hasApplied ? (
                    <label className="subsidies-field subsidies-field--animated">
                      <FieldLabel
                        t={t}
                        tooltip={t(
                          "fields.applicationDate.tooltip",
                          "Date on which the Homebuyer submitted the subsidy or grant application.",
                        )}
                      >
                        {t(
                          "fields.applicationDate.label",
                          "Date of application",
                        )}
                      </FieldLabel>

                      <div className="subsidies-date-input">
                        <input
                          type="date"
                          value={form.application_date}
                          onChange={(event) =>
                            updateForm("application_date", event.target.value)
                          }
                        />

                        <CalendarDays size={17} />
                      </div>

                      {formErrors.application_date ? (
                        <span className="subsidies-field-error">
                          {formErrors.application_date}
                        </span>
                      ) : null}
                    </label>
                  ) : null}

                  {/* GRANTED */}

                  {isGranted ? (
                    <div className="subsidies-conditional-group">
                      <label className="subsidies-field subsidies-field--animated">
                        <FieldLabel
                          t={t}
                          tooltip={t(
                            "fields.awardedDate.tooltip",
                            "Date on which the subsidy or grant was formally awarded.",
                          )}
                        >
                          {t("fields.awardedDate.label", "Date awarded")}
                        </FieldLabel>

                        <div className="subsidies-date-input">
                          <input
                            type="date"
                            value={form.awarded_date}
                            onChange={(event) =>
                              updateForm("awarded_date", event.target.value)
                            }
                          />

                          <CalendarDays size={17} />
                        </div>

                        {formErrors.awarded_date ? (
                          <span className="subsidies-field-error">
                            {formErrors.awarded_date}
                          </span>
                        ) : null}
                      </label>

                      <label className="subsidies-field subsidies-field--animated">
                        <FieldLabel
                          t={t}
                          tooltip={t(
                            "fields.grantedAmount.tooltip",
                            "Final amount of public funding that has been formally granted to the renovation project.",
                          )}
                        >
                          {t("fields.grantedAmount.label", "Amount granted")}
                        </FieldLabel>

                        <div className="subsidies-money-input">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.granted_amount}
                            placeholder="0.00"
                            onChange={(event) =>
                              updateForm("granted_amount", event.target.value)
                            }
                          />

                          <Euro size={16} />
                        </div>

                        {formErrors.granted_amount ? (
                          <span className="subsidies-field-error">
                            {formErrors.granted_amount}
                          </span>
                        ) : null}
                      </label>
                    </div>
                  ) : null}

                  {/* REJECTED */}

                  {isRejected ? (
                    <label className="subsidies-field subsidies-field--animated">
                      <FieldLabel
                        t={t}
                        tooltip={t(
                          "fields.denialDate.tooltip",
                          "Date on which the subsidy or grant application was formally rejected.",
                        )}
                      >
                        {t("fields.denialDate.label", "Date of denial")}
                      </FieldLabel>

                      <div className="subsidies-date-input">
                        <input
                          type="date"
                          value={form.denial_date}
                          onChange={(event) =>
                            updateForm("denial_date", event.target.value)
                          }
                        />

                        <CalendarDays size={17} />
                      </div>

                      {formErrors.denial_date ? (
                        <span className="subsidies-field-error">
                          {formErrors.denial_date}
                        </span>
                      ) : null}
                    </label>
                  ) : null}

                  {/* NOTES */}

                  <label className="subsidies-field">
                    <FieldLabel
                      t={t}
                      tooltip={t(
                        "fields.notes.tooltip",
                        "Optional notes about eligibility, pending documentation, application progress or the funding decision.",
                      )}
                    >
                      {t("fields.notes.label", "Notes")}
                    </FieldLabel>

                    <textarea
                      rows={3}
                      value={form.notes}
                      placeholder={t(
                        "fields.notes.placeholder",
                        "Add any relevant notes...",
                      )}
                      onChange={(event) =>
                        updateForm("notes", event.target.value)
                      }
                    />
                  </label>
                </div>

                <div className="subsidies-modal__actions">
                  <button
                    type="button"
                    className="subsidies-modal-button subsidies-modal-button--secondary"
                    onClick={closeModal}
                  >
                    {t("buttons.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="subsidies-modal-button subsidies-modal-button--primary"
                    onClick={saveItem}
                    disabled={
                      savingItem || (!editingId && !addableFundingTypes.length)
                    }
                  >
                    {savingItem ? (
                      <LoaderCircle size={16} />
                    ) : (
                      <Check size={16} />
                    )}

                    {savingItem
                      ? t("buttons.saving", "Saving...")
                      : editingId
                        ? t("buttons.update", "Update")
                        : t("buttons.save", "Save")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* ===================================================
          DELETE MODAL
          =================================================== */}

      {deleteTarget
        ? createPortal(
            <div
              className={`subsidies-modal-backdrop ${
                deleteClosing ? "is-closing" : ""
              }`}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeDeleteModal();
                }
              }}
            >
              <div
                className={`subsidies-delete-modal ${
                  deleteClosing ? "is-closing" : ""
                }`}
                role="dialog"
                aria-modal="true"
              >
                <span className="subsidies-delete-modal__icon">
                  <Trash2 size={23} />
                </span>

                <h2>{t("deleteModal.title", "Remove subsidy?")}</h2>

                <p>
                  {t(
                    "deleteModal.description",
                    "This funding support will be removed from the project. You can add it again later if needed.",
                  )}
                </p>

                <strong>{deleteTarget.funding_type_name}</strong>

                <div className="subsidies-delete-modal__actions">
                  <button type="button" onClick={closeDeleteModal}>
                    {t("buttons.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="is-danger"
                    onClick={confirmDelete}
                    disabled={deletingItem}
                  >
                    {deletingItem ? (
                      <LoaderCircle size={15} />
                    ) : (
                      <Trash2 size={15} />
                    )}

                    {deletingItem
                      ? t("buttons.removing", "Removing...")
                      : t("buttons.remove", "Remove")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
